import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Mail, 
  Building2, 
  Clock, 
  Trash2, 
  CheckCircle2,
  MessageSquare,
  XCircle,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [submissionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/submissions`),
        axios.get(`${API}/admin/stats`)
      ]);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateStatus = async (submissionId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/submissions/${submissionId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
      console.error("Error updating status:", error);
    }
  };

  const deleteSubmission = async (submissionId) => {
    try {
      await axios.delete(`${API}/admin/submissions/${submissionId}`);
      toast.success("Submission deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete submission");
      console.error("Error deleting submission:", error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      contacted: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      closed: "bg-green-500/20 text-green-400 border-green-500/30"
    };
    return styles[status] || styles.new;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-container min-h-screen bg-[#050505]" data-testid="admin-panel">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
          }
        }}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <motion.a
            href="/"
            className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            whileHover={{ scale: 1.05 }}
            data-testid="back-to-home"
          >
            <ArrowLeft size={20} className="text-white/60" />
          </motion.a>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-white/40 text-sm">Manage contact submissions</p>
          </div>
        </div>

        <motion.button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-white/70"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          Refresh
        </motion.button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        <div className="stat-card" data-testid="stat-total">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Submissions</div>
        </div>
        <div className="stat-card" data-testid="stat-new">
          <div className="stat-number text-blue-400">{stats.new}</div>
          <div className="stat-label">New</div>
        </div>
        <div className="stat-card" data-testid="stat-contacted">
          <div className="stat-number text-yellow-400">{stats.contacted}</div>
          <div className="stat-label">Contacted</div>
        </div>
        <div className="stat-card" data-testid="stat-closed">
          <div className="stat-number text-green-400">{stats.closed}</div>
          <div className="stat-label">Closed</div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="admin-card" data-testid="submissions-table-container">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-20" data-testid="no-submissions">
            <MessageSquare size={48} className="mx-auto text-white/20 mb-4" />
            <p className="text-white/40">No submissions yet</p>
            <p className="text-white/30 text-sm mt-2">Contact form submissions will appear here</p>
          </div>
        ) : (
          <Table data-testid="submissions-table">
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Contact</TableHead>
                <TableHead className="text-white/50">Business</TableHead>
                <TableHead className="text-white/50">Message</TableHead>
                <TableHead className="text-white/50">Date</TableHead>
                <TableHead className="text-white/50">Status</TableHead>
                <TableHead className="text-white/50 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow 
                  key={submission.id} 
                  className="border-white/5 hover:bg-white/5"
                  data-testid={`submission-row-${submission.id}`}
                >
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{submission.name}</p>
                      <a 
                        href={`mailto:${submission.email}`} 
                        className="text-white/40 text-sm hover:text-white/60 flex items-center gap-1"
                      >
                        <Mail size={12} />
                        {submission.email}
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-white/60">
                      <Building2 size={14} />
                      {submission.business_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-white/60 text-sm max-w-xs truncate">
                      {submission.message}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-white/40 text-sm">
                      <Clock size={14} />
                      {formatDate(submission.created_at)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={submission.status}
                      onValueChange={(value) => updateStatus(submission.id, value)}
                    >
                      <SelectTrigger 
                        className={`w-32 border ${getStatusBadge(submission.status)} bg-transparent`}
                        data-testid={`status-select-${submission.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="new" className="text-blue-400">New</SelectItem>
                        <SelectItem value="contacted" className="text-yellow-400">Contacted</SelectItem>
                        <SelectItem value="closed" className="text-green-400">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <motion.button
                          className="p-2 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          data-testid={`delete-btn-${submission.id}`}
                        >
                          <Trash2 size={16} />
                        </motion.button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Submission?</AlertDialogTitle>
                          <AlertDialogDescription className="text-white/50">
                            This will permanently delete the submission from {submission.name}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteSubmission(submission.id)}
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30"
                            data-testid={`confirm-delete-${submission.id}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
